'use client';

import * as React from 'react';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import { cn } from '@/lib/utils';

/**
 * Separator Component
 *
 * A flexible separator component for dividing content horizontally or vertically.
 * Built on Radix UI's Separator primitive with design token integration.
 *
 * @example
 * ```tsx
 * // Horizontal separator (default)
 * <Separator />
 *
 * // Vertical separator
 * <div className="flex gap-4">
 *   <div>Content 1</div>
 *   <Separator orientation="vertical" className="h-12" />
 *   <div>Content 2</div>
 * </div>
 *
 * // Custom styling
 * <Separator className="bg-primary-500 h-1" />
 *
 * // With decorative content
 * <Separator decorative />
 * ```
 */
const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(
  (
    {
      className,
      orientation = 'horizontal',
      decorative = true,
      ...props
    },
    ref
  ) => (
    <SeparatorPrimitive.Root
      ref={ref}
      decorative={decorative}
      orientation={orientation}
      className={cn(
        'shrink-0 bg-border-primary',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className
      )}
      {...props}
    />
  )
);

Separator.displayName = SeparatorPrimitive.Root.displayName;

export { Separator };
export type { SeparatorProps } from '@radix-ui/react-separator';
